// src/components/WarRoomOverview.js
//
// SECTION 540. Bob, 2026-09-30, on the War Room opening on an empty list: "when it opens, the room looks empty and barren
// of any activity". A General Chairperson's meeting room is never empty - his committee's business is waiting for it. The
// room now opens on that business: claims at his desk, deadlines in the next two weeks, open action items from earlier
// meetings, the last meeting, and a "Suggested for the next meeting" list he ticks to convene with those claims already on
// the agenda. Everything here is read from what he can already read; nothing is written until he convenes.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { claimLabel } from '../data/warRoom';
import { roomOverview } from '../data/warRoomOverview';
import { ActionRow } from './WarRoomRecord';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', red: '#C0392B' };
export default function WarRoomOverview({ committee, claims, sessions, onConveneWith, mayConvene }) {
  const [actions, setActions] = useState([]);
  const [picked, setPicked] = useState([]);
  // Forensic audit 2026-10-16: a failed read used to become [] and the room said "None open." It now says it could not read.
  const [actionsErr, setActionsErr] = useState('');
  const [actionsLoaded, setActionsLoaded] = useState(false);
  useEffect(() => {
    setActionsErr(''); setActionsLoaded(false);
    if (!committee) return undefined;
    const q = query(collection(db, 'warRoomActions'), where('committee', '==', committee));
    const unsub = onSnapshot(q,
      snap => { setActions(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setActionsErr(''); setActionsLoaded(true); },
      e => { setActions([]); setActionsErr('The action items could not be read (' + ((e && (e.code || e.message)) || 'refused') + '). This is NOT "none open" - reload the page.'); setActionsLoaded(true); });
    return () => unsub();
  }, [committee]);
  const o = useMemo(() => roomOverview(claims || [], sessions || [], actions), [claims, sessions, actions]);
  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : p.concat([id]));
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{committee} - for the next meeting</div>
      {/* section 544: the count tiles moved to the War Room's header; the deadlines to its Clocks tab. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        <div style={{ background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Suggested for the next meeting</div>
          {claims === null ? <div style={{ fontSize: 12, color: C.dim }}>Reading the committee's claims…</div> : o.suggested.length === 0 ? (
            <div style={{ fontSize: 12, color: C.dim }}>No claim is at your desk and no deadline falls in the next 14 days.</div>
          ) : o.suggested.map(({ claim, why }) => {
            const id = String(claim.docId || claim.id);
            return (
              <label key={id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, color: C.text, padding: '3px 0', cursor: mayConvene ? 'pointer' : 'default' }}>
                {mayConvene ? <input aria-label="Include this item in the meeting" type="checkbox" checked={picked.includes(id)} onChange={() => togglePick(id)} /> : null}
                <span>{claimLabel(claim)}<span style={{ color: C.dim }}> - {why}</span></span>
              </label>
            );
          })}
          {mayConvene && o.suggested.length ? (
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8, background: C.red, borderColor: C.red }} disabled={!picked.length}
              onClick={() => onConveneWith && onConveneWith(o.suggested.map(x => x.claim).filter(c => picked.includes(String(c.docId || c.id))))}>
              Convene with {picked.length || 'the ticked'} claim{picked.length === 1 ? '' : 's'} on the agenda
            </button>
          ) : null}
        </div>

        <div style={{ background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Open action items</div>
          {actionsErr ? <div style={{ fontSize: 12, color: C.bad }}>&#9888; {actionsErr}</div>
            : !actionsLoaded ? <div style={{ fontSize: 12, color: C.dim }}>Reading…</div>
            : o.openActions.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>None open.</div> : o.openActions.slice(0, 8).map(a => <ActionRow key={a.id} a={a} showOccasion />)}
        </div>
      </div>
    </div>
  );
}
